import type { Column } from '../../../../../claim-history/simple-table'
import type {
  Admission,
  AdmissionCode,
  AdmissionFormState,
  CareTeamMember,
  AdmissionCreateTableFormProps,
  AdmissionDetailsProps,
  AdmissionDetailsValues,
  AdmissionUpdateTableFormProps
} from '../types'
import type { Dispatch, SetStateAction } from 'react'

import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import _ from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'
import Select from 'react-select'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
import Admissions from 'shared_src/dictionaries/encounters/AdmissionCodes'

import http from '../../../../../../lib/http'
import { useAppSelector } from '../../../../../../store'
import * as Table from '../../../../../claim-history/simple-table'
import { useInvalidateIGAO } from '../../../../../igao/hooks/invalidate-igao'
import FriendlyDate from '../../../../../shared/Date/FriendlyDate'
import * as Grid from '../../../../../shared/grid'
import { MaterialDeleteButton } from '../../../../../shared/material-delete-button'
import { Button, Checkbox, PortedModal } from '../../../../../shared/NarusMaterial'

import { CareTeamMemberSelect } from './care-team-member-select'
import { useCreateAdmissionMutation } from './mutations/create-admission-mutation'
import { useDeleteAdmissionMutation } from './mutations/delete-admission-mutation'

const admissionCodesOptions: AdmissionCode[] = (Admissions as Array<{code: string, label: string}>).map(({ code, label }) => {
  return {
    value: code,
    label
  }
})

export interface Response {
  entries: Admission[]
  empty: boolean
}

type AdmissionsQueryKeys = [
  string,
  string
]

interface ModalState {
  open: boolean
  title: string
  actions: JSX.Element[]
}

interface AdmissionGridProps {
  createFormState: AdmissionFormState
  setCreateFormState: Dispatch<SetStateAction<AdmissionFormState>>
}

export function AdmissionGrid ({ createFormState, setCreateFormState }: AdmissionGridProps): JSX.Element {
  let invalidateIGAO = useInvalidateIGAO('admissions')
  let queryClient = useQueryClient()
  let [ modal, setModal ] = useState<ModalState>()
  let patientId = useAppSelector(state => state.globalVars?.params?.id)

  const query = useQuery<Response, unknown, Response, AdmissionsQueryKeys>({
    queryKey: [
      'admissions-grid',
      patientId as string
    ],
    queryFn: async () => {
      async function getAdmissions (): Promise<Response> {
        return await http.getRequest(`/patients/${patientId as string}/admissions`)
      }

      let response = await getAdmissions()

      return {
        ...response,
        entries: response.entries.map((entry) => ({
          ...entry,
          physician: entry.physician
            ? Object.keys(entry.physician).length > 0
              ? entry.physician
              : null
            : null
        }))
      }
    },
    refetchOnWindowFocus: false
  })

  let isEmpty = query.data?.empty ?? false

  let createAdmission = useCreateAdmissionMutation({ isEmpty, patientId })

  const refetch = useCallback(async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: [ 'admissions-grid', patientId as string ] })
    await invalidateIGAO()
    await queryClient.invalidateQueries({
      predicate ({ queryKey }) {
        return Array.isArray(queryKey) && queryKey[0] === 'claims-history/admissions'
      }
    })
  }, [ invalidateIGAO, patientId, queryClient ])

  const deleteAdmission = useDeleteAdmissionMutation({ patientId })
  const handleUpdateAdmission = useCallback(async (id: string, value: AdmissionDetailsValues): Promise<void> => {
    if (!patientId) {
      return
    }

    http.putRequest(`/patients/${patientId}/admission/${id}`, value)
      .then(() => {
        refetch().catch((e) => console.error(e))
      }).catch((e: unknown) => {
        console.error(e)
      })
  }, [ patientId, refetch ])

  function handlePatientDeniesRecentAdmissions (value: boolean): void {
    if (!patientId) return

    http.putRequest(`/patients/${patientId}/admissions`, { empty: value })
      .then(() => {
        refetch().catch((e) => console.error(e))
      }).catch((e: unknown) => {
        console.error(e)
      })
  }

  const columns: Array<Column<Admission>> = useMemo(() => [
    {
      name: 'facility',
      header: 'Facility'
    }, {
      name: 'type',
      header: 'Type',
      getValue: (x) => x.type?.display
    }, {
      name: 'physician',
      header: 'Physician',
      getValue: (x) => x.physician?.display
    }, {
      name: 'admissionDate',
      header: 'Admission Date',
      gridArea: '0.8fr',
      getValue: (x) => (x.admissionDate ? <FriendlyDate date={x.admissionDate} /> : null)
    }, {
      name: 'dischargeDate',
      header: 'Discharge Date',
      gridArea: '0.8fr',
      getValue: (x) => (x.dischargeDate ? <FriendlyDate date={x.dischargeDate} /> : null)
    }, {
      name: 'anticipatedDischargeDate',
      header: 'Anticipated Discharge Date',
      getValue: (x) => (x.anticipatedDischargeDate ? <FriendlyDate date={x.anticipatedDischargeDate} /> : null)
    },
    {
      name: 'actions',
      header: 'Actions',
      headerStyle: {
        justifyContent: 'center'
      },
      style: {
        display: 'flex',
        justifyContent: 'center'
      },
      gridArea: '0.4fr',
      getValue: function Action (item) {
        return (
          <MaterialDeleteButton
            onClick={() => {
              setModal({
                title: 'Are you sure you want to delete this admission?',
                open: true,
                actions: [
                  <Button
                    key={1}
                    data-cy='modal_cancel-btn'
                    label='Cancel'
                    // @ts-expect-error // It's fine
                    onClick={() => {
                      setModal(undefined)
                    }}
                    grey
                  />,
                  <Button
                    key={2}
                    label='Delete'
                    data-cy='modal_discard-btn'
                    // @ts-expect-error // It's fine
                    onClick={() => {
                      deleteAdmission.mutate({ id: item.id })

                      setModal(undefined)
                    }}
                  />
                ]
              })
            }}
            style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', cursor: 'pointer' }}
          />
        )
      }
    }], [ deleteAdmission ])

  return (
    <>
      <div style={{ backgroundColor: '#fff', borderRadius: '1rem' }}>
        <Table.Table
          header={(
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', flex: 1, alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                  <h2>Admissions</h2>
                  <div style={{ fontSize: '1rem', fontWeight: '400', color: '#3D3D3D' }}>
                    <Checkbox
                      label='Patient denies any recent admissions'
                      value={query.data?.empty}
                      disabled={query.isLoading || (query.data?.entries?.length ?? 0) > 0}
                      onChange={(val: boolean) => handlePatientDeniesRecentAdmissions(val)}
                      className={undefined}
                      labelClassName={undefined}
                    />
                  </div>
                </div>
                {!createFormState.open && (
                  <Grid.Button
                    onClick={() => setCreateFormState((prev) => ({ ...prev, open: true }))}
                    style={{ border: '1px solid #4D1EE6' }}
                    rounded
                  >
                    Add Admission
                  </Grid.Button>
                )}
              </div>
              {createFormState.open && (
                <div style={{ padding: '4rem 3rem 1rem 0' }}>
                  <CreateTableForm
                    initialState={createFormState.form}
                    onSubmit={(values) => {
                      createAdmission.mutate(values)
                      setCreateFormState(() => ({ open: false }))
                    }}
                    onCancel={() => setCreateFormState(() => ({ open: false }))}
                  />
                </div>
              )}
            </div>
          )}
          noDataPlaceholder={query.data?.entries.length === 0 ? 'This patient has no admissions' : undefined}
          columns={columns}
          data={query.data?.entries ?? []}
          error={query.error as Error}
          queryStatus={query.status}
          fetchStatus={query.fetchStatus}
          expandingComponent={(item, _, setState) => (
            <UpdateTableForm
              onSubmit={(value) => {
                handleUpdateAdmission(item.id, value)
                  .then(() => {
                    setState(null)
                  }).catch((e) => {
                    console.error(e)
                  })
              }}
              item={item}
              onCancel={() => setState(null)}
            />
          )}
          disableMinHeight
        />
      </div>

      <PortedModal
        open={modal?.open}
        title={modal?.title}
        actions={modal?.actions}
      />
    </>
  )
}

const defaultValue = {
  admissionCode: undefined,
  facility: '',
  reason: '',
  admissionDate: '',
  dischargeDate: '',
  anticipatedDischargeDate: '',
  physician: null,
  procedureCode: null
}

export function Form ({ item, onSubmit, onCancel, initialState }: AdmissionDetailsProps): JSX.Element {
  let patientId = useAppSelector(state => state.globalVars?.params?.id) as string

  const [ values, setValues ] = useState<AdmissionDetailsValues>(defaultValue)
  const [ initialValues, setInitialValues ] = useState<AdmissionDetailsValues | undefined>(defaultValue)

  useEffect(() => {
    const mapped = initialState
      ? {
        admissionCode: initialState?.admissionCode,
        facility: initialState?.facility ?? '',
        reason: initialState?.reason ?? '',
        admissionDate: initialState?.admissionDate ?? '',
        dischargeDate: '',
        anticipatedDischargeDate: '',
        physician: null,
        procedureCode: initialState?.procedureCode ?? null
      }
      : {
        admissionCode: admissionCodesOptions.find((admissionType) => admissionType.value === item?.type?.code)?.value,
        facility: item?.facility ?? '',
        reason: item?.reason ?? '',
        admissionDate: item?.admissionDate ?? '',
        dischargeDate: item?.dischargeDate ?? '',
        anticipatedDischargeDate: item?.anticipatedDischargeDate ?? '',
        physician: item?.physician ?? null,
        procedureCode: (item?.procedureCode as string | null | undefined) ?? null
      }

    setValues(mapped)
    setInitialValues(initialState ? undefined : mapped)
  }, [ initialState, item ])

  function handleChange (key: keyof AdmissionDetailsValues, value: string | undefined): void {
    setValues((prev) => ({
      ...prev,
      [key]: value
    }))
  }

  const { data: careTeamMembers } = useQuery<CareTeamMember[], unknown, CareTeamMember[], [string]>({
    queryKey: [ patientId ],
    queryFn: async () => {
      return await http.getRequest(`/patients/${patientId}/careNetwork`)
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false
  })

  const admissionTypeValue = values.admissionCode
    ? {
      value: values.admissionCode,
      label: admissionCodesOptions.find((admissionType) => values.admissionCode === admissionType.value)?.label
    }
    : null

  const saveButtonDisabled = values.facility.length <= 0 || (values.admissionCode?.length ?? 0) <= 0 || _.isEqual(initialValues, values)

  return (
    <div
      className='admission-form-wrapper'
      style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}
    >
      <div style={{ display: 'flex', gap: '2rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: '2.5rem', columnGap: '2rem', flex: 2 }}>
          <Grid.SelectWrapper
            label='Admission Type'
            data-test='admission-type-select'
            required
          >
            <Select
              options={admissionCodesOptions}
              value={admissionTypeValue}
              onChange={(v) => handleChange('admissionCode', v?.value)}
              placeholder='Admission Type'
              required
              instanceId='admission-type-select'
              styles={{
                control: (styles: any, { isFocused }) => ({
                  ...styles,
                  backgroundColor: '#fff',
                  border: '1px solid #DADCE0',
                  borderRadius: '4px',
                  boxShadow: 'none',
                  height: '2.6rem',

                  outline: isFocused ? '#5404FF solid 2px' : 'none'
                }),
                container: (styles: any, { isFocused }) => ({
                  ...styles,
                  flex: 1,
                  backgroundColor: '#F6F6F6',
                  boxShadow: 'none',
                  borderRadius: '8px',
                  zIndex: isFocused ? 10 : 5
                }),
                indicatorSeparator: (styles: any) => ({
                  ...styles,
                  marginBottom: 0,
                  marginTop: 0
                }),
                dropdownIndicator: (styles: any) => ({
                  ...styles,
                  padding: '0 18px'
                })
              }}
            />
          </Grid.SelectWrapper>
          <Grid.Input
            required
            placeholder='Facility Name'
            value={values.facility}
            onChange={(value) => handleChange('facility', value)}
          />

          <CareTeamMemberSelect
            placeholder='Admitting Physician'
            careTeamMembers={careTeamMembers}
            value={values.physician}
            instanceId='admission-physician-select'
            onChange={(value) => {
              setValues((prev) => ({ ...prev, physician: value }))
            }}
          />
          <Grid.Input
            type='date'
            placeholder='Admission Date'
            value={values.admissionDate ?? ''}
            onChange={value => handleChange('admissionDate', value)}
          />
          <Grid.Input
            type='date'
            placeholder='Discharge Date'
            value={values.dischargeDate ?? ''}
            onChange={value => handleChange('dischargeDate', value)}
          />
          <Grid.Input
            type='date'
            placeholder='Anticipated Discharge Date'
            value={values.anticipatedDischargeDate ?? ''}
            onChange={value => handleChange('anticipatedDischargeDate', value)}
          />
        </div>
        <div style={{ display: 'flex', flex: 1 }}>
          <Grid.Textarea
            placeholder='Reason for Admission'
            value={values.reason}
            onChange={(e) => handleChange('reason', e.target.value)}
          />
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: '0.4rem'
        }}
      >
        <Grid.Button
          disabled={saveButtonDisabled}
          onClick={() => onSubmit(values)}
          style={{
            backgroundColor: '#EAE4FC',
            color: '#6D45F1',
            padding: '.8rem 3rem'
          }}
        >
          {item ? 'Save' : 'Create'}
        </Grid.Button>
        <Grid.Button
          onClick={onCancel}
          light
          style={{ padding: '.8rem 3rem' }}
        >
          Cancel
        </Grid.Button>
      </div>

    </div>
  )
}

export function CreateTableForm ({ initialState, onCancel, onSubmit }: AdmissionCreateTableFormProps): JSX.Element {
  return (
    <Form
      initialState={initialState}
      onSubmit={onSubmit}
      onCancel={onCancel}
    />
  )
}

export function UpdateTableForm ({ item, onCancel, onSubmit }: AdmissionUpdateTableFormProps): JSX.Element {
  return (
    <Form
      item={item}
      onSubmit={onSubmit}
      onCancel={onCancel}
    />
  )
}
